import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Layout } from '../components/Layout.tsx';
import { reportsAPI, territoriesAPI, regionAPI } from '../services/api.ts';
import {
  ArrowDownTrayIcon,
  FunnelIcon,
  CalendarDaysIcon,
  MapPinIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const months = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

const years = Array.from({ length: 5 }, (_, i) =>
  (new Date().getFullYear() - i).toString(),
);

export const Reports: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Filters
  const [selectedMonth, setSelectedMonth] = useState(
    months[new Date().getMonth()].value,
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const [selectedTerritory, setSelectedTerritory] = useState<number | ''>('');
  const [selectedRegion, setSelectedRegion] = useState<number | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [isResending, setIsResending] = useState(false);

  // Data for filters
  const [territories, setTerritories] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);

  const fetchFiltersData = async () => {
    try {
      const response = await reportsAPI.getFilters();

      if (response.success && response.data) {
        setRegions(response.data.regions || []);
        setTerritories(response.data.territories || []);
      }
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reportsAPI.getAll({
        month: selectedMonth,
        year: selectedYear,
        territoryId: selectedTerritory || undefined,
        regionId: selectedRegion || undefined,
        status: selectedStatus || undefined,
      });

      if (response.success) {
        setReports(response.data);
      } else {
        setError(response.message || 'Failed to fetch reports');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching reports');
    } finally {
      setLoading(false);
    }
  }, [
    selectedMonth,
    selectedYear,
    selectedTerritory,
    selectedRegion,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Reset territory when region changes
  useEffect(() => {
    setSelectedTerritory('');
  }, [selectedRegion]);

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkResend = async () => {
    if (selectedReportIds.length === 0) return;

    // Filter only approved reports (UI should already handle this, but for safety)
    const approvedReports = reports.filter(
      (r) => selectedReportIds.includes(r._id) && r.status === 'approved',
    );

    if (approvedReports.length === 0) {
      toast.error('Only approved reports can be resent');
      return;
    }

    try {
      setIsResending(true);
      const res = await reportsAPI.bulkResendEmails(
        approvedReports.map((r) => r._id),
      );
      if (res.success) {
        setSelectedReportIds([]);
      }
    } catch (error) {
      console.error('Bulk resend error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedReportIds.length === reports.length) {
      setSelectedReportIds([]);
    } else {
      setSelectedReportIds(reports.map((r) => r._id));
    }
  };

  const toggleSelectReport = (id: string) => {
    setSelectedReportIds((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id],
    );
  };

  const selectedApprovedReportsCount = reports.filter(
    (r) => selectedReportIds.includes(r._id) && r.status === 'approved',
  ).length;

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Expense Reports
            </h1>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              A list of all expense reports for your company with filtering
              options.
            </p>
          </div>
          {selectedReportIds.length > 0 && (
            <div className="mt-4 sm:ml-16 sm:mt-0 flex items-center gap-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedReportIds.length} selected (
                {selectedApprovedReportsCount} approved)
              </span>
              <button
                onClick={handleBulkResend}
                disabled={selectedApprovedReportsCount === 0 || isResending}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedApprovedReportsCount > 0 && !isResending
                    ? 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                }`}
              >
                <FunnelIcon className="h-4 w-4" />
                Resend Emails
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700/50 backdrop-blur-xl">
          <div className="flex items-center gap-2 mb-6 border-b border-gray-100 dark:border-gray-700 pb-3">
            <FunnelIcon className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Filters
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                <CalendarDaysIcon className="h-4 w-4" />
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="block w-full rounded-xl border-gray-200 py-3 px-4 dark:border-gray-700 dark:bg-gray-900/50 dark:text-white shadow-sm focus:border-primary focus:ring-primary/20 transition-all sm:text-base hover:border-primary/50 cursor-pointer"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-medium text-gray-600 dark:text-gray-400">
                <CalendarDaysIcon className="h-5 w-5" />
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full rounded-xl border-gray-200 py-3 px-4 dark:border-gray-700 dark:bg-gray-900/50 dark:text-white shadow-sm focus:border-primary focus:ring-primary/20 transition-all sm:text-base hover:border-primary/50 cursor-pointer"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                <GlobeAltIcon className="h-5 w-5" />
                Region
              </label>
              <select
                value={selectedRegion}
                onChange={(e) =>
                  setSelectedRegion(
                    e.target.value ? Number(e.target.value) : '',
                  )
                }
                className="block w-full rounded-xl border-gray-200 py-3 px-4 dark:border-gray-700 dark:bg-gray-900/50 dark:text-white shadow-sm focus:border-primary focus:ring-primary/20 transition-all sm:text-base hover:border-primary/50 cursor-pointer"
              >
                <option value="">All Regions</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                <MapPinIcon className="h-5 w-5" />
                Territory
              </label>
              <select
                value={selectedTerritory}
                onChange={(e) =>
                  setSelectedTerritory(
                    e.target.value ? Number(e.target.value) : '',
                  )
                }
                className="block w-full rounded-xl border-gray-200 py-3 px-4 dark:border-gray-700 dark:bg-gray-900/50 dark:text-white shadow-sm focus:border-primary focus:ring-primary/20 transition-all sm:text-base hover:border-primary/50 cursor-pointer"
              >
                <option value="">All Territories</option>
                {territories
                  .filter(
                    (t) => !selectedRegion || t.region_id === selectedRegion,
                  )
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                <FunnelIcon className="h-5 w-5" />
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="block w-full rounded-xl border-gray-200 py-3 px-4 dark:border-gray-700 dark:bg-gray-900/50 dark:text-white shadow-sm focus:border-primary focus:ring-primary/20 transition-all sm:text-base hover:border-primary/50 cursor-pointer"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center h-64 bg-white dark:bg-gray-800">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : error ? (
                  <div className="text-center py-12 bg-white dark:bg-gray-800">
                    <p className="text-red-500">{error}</p>
                    <button
                      onClick={fetchReports}
                      className="mt-4 text-primary hover:text-primary-dark"
                    >
                      Try again
                    </button>
                  </div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-gray-800">
                    <p className="text-gray-500 dark:text-gray-400">
                      No reports found for the selected filters.
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          <input
                            type="checkbox"
                            checked={
                              reports.length > 0 &&
                              selectedReportIds.length === reports.length
                            }
                            onChange={toggleSelectAll}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-700 dark:bg-gray-900"
                          />
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Sender
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Region
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Territories
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Project
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Month/Year
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Created At
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6 text-right text-sm font-semibold text-gray-900 dark:text-white">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {reports.map((report) => (
                        <tr
                          key={report._id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            selectedReportIds.includes(report._id)
                              ? 'bg-primary/5 dark:bg-primary/5'
                              : ''
                          }`}
                        >
                          <td className="px-3 py-4 text-sm">
                            <input
                              type="checkbox"
                              checked={selectedReportIds.includes(report._id)}
                              onChange={() => toggleSelectReport(report._id)}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-700 dark:bg-gray-900"
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-white font-medium">
                            <div className="flex flex-col">
                              <span>
                                {report.sender_details?.name || 'N/A'}
                              </span>
                              <span className="text-xs text-gray-500 font-normal">
                                ID: {report.sender}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium">
                              {report.sender_details?.region?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {report.territory_details &&
                              report.territory_details.length > 0
                                ? report.territory_details.map((t: any) => (
                                    <span
                                      key={t.id}
                                      className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-[10px]"
                                    >
                                      {t.name}
                                    </span>
                                  ))
                                : report.sender_details?.territories &&
                                    report.sender_details.territories.length > 0
                                  ? report.sender_details.territories.map(
                                      (t: any) => (
                                        <span
                                          key={t.id}
                                          className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-[10px]"
                                        >
                                          {t.name}
                                        </span>
                                      ),
                                    )
                                  : 'N/A'}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {report.project_details?.name || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {months.find(
                              (m) => m.value === report.selectedMonth,
                            )?.label || report.selectedMonth}{' '}
                            {report.selectedYear}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                report.status === 'approved'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : report.status === 'rejected'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                              }`}
                            >
                              {report.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(report.createdAt).toLocaleDateString()}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                              onClick={() =>
                                handleDownload(
                                  report.reportURL,
                                  `Report-${report.sender_details?.name}-${report.selectedMonth}.pdf`,
                                )
                              }
                              className="text-primary hover:text-primary-dark dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center gap-1"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
